import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ShareDialog({ open, onOpenChange, item }) {
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState("read");
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vault/${item.id}/shares`);
      setShares(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load shares");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setEmail("");
      setPermission("read");
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/vault/${item.id}/share`, { email: email.trim(), permission });
      toast.success(`Shared with ${email.trim()}`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Share failed");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (shareId) => {
    try {
      await api.delete(`/shares/${shareId}`);
      toast.success("Access revoked");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Revoke failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl flex items-center gap-2">
            <Users size={18} /> Share "{item?.title}"
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Recipient email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="wife@example.com"
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
              data-testid="share-email-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Permission</Label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1" data-testid="share-permission-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="read">Read only</SelectItem>
                <SelectItem value="edit">Can edit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
            data-testid="share-submit-btn"
          >
            {submitting ? "Sharing…" : "Share"}
          </Button>
        </form>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)] mb-2">
            Currently shared with
          </div>
          {loading ? (
            <div className="text-sm text-[color:var(--vault-text-tertiary)]">Loading…</div>
          ) : shares.length === 0 ? (
            <div className="text-sm text-[color:var(--vault-text-tertiary)]" data-testid="no-shares-msg">No one yet.</div>
          ) : (
            <ul className="space-y-2" data-testid="shares-list">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center justify-between rounded-xl border border-[color:var(--vault-border)] px-3 py-2">
                  <div>
                    <div className="text-sm font-medium">{s.shared_with_email}</div>
                    <div className="text-xs text-[color:var(--vault-text-tertiary)] capitalize">{s.permission}</div>
                  </div>
                  <button
                    onClick={() => revoke(s.id)}
                    className="p-2 text-[color:var(--vault-error)] hover:bg-[color:var(--vault-error-bg)] rounded-lg"
                    title="Revoke"
                    data-testid={`revoke-share-${s.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="share-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
